import { z } from 'zod';
import {
  addressSchema,
  createDateSchema,
  createOptionalDateSchema,
  deadLoadSchema,
  jobNameSchema,
  liveLoadSchema,
  poSchema,
  snowLoadSchema,
  stateSchema,
  windLoadSchema,
} from '../utils';
import { Address, DealerLocation } from './location-types.ts';

export interface EngineeringFees {
  manufacturerId: number;
  openPoleBarnFee: number;
  partialClosedPoleBarnFee: number;
  fullyEnclosedPoleBarnFee: number;
  permanentDwellingFee: number;
}

export interface JobLocation {
  address1: string;
  address2?: string;
  city: string;
  state: string;
  zip: string;
  latitude?: number;
  longitude?: number;
  // 'override' = jobsite captured while Address Validation Override was active
  // (not Google/ASCE validated, but still recorded on the order).
  validationStatus: 'none' | 'validated' | 'failed' | 'override';
  validationMode: 'address' | 'coordinates';
}

export interface AsceData {
  windLoadMph: number;
  snowLoadPsf: number;
  elevation?: string;
  riskLevel: number;
  isHurricaneWindDebrisZone?: boolean;
  isSpecialWindZone?: boolean;
  isNewHampshire_7_16?: boolean;
  mriWindResults?: Record<string, string>;
  lastUpdated?: string;
}

export interface Order {
  id?: number;
  orderNumber: string;
  dealerId: number;
  po: string;
  orderDate: string;
  dueDate?: string;
  jobName: string;
  status: number;
  billingAddress: Address;
  shippingAddress?: Address;
  pickup?: boolean;
  buildingState: string;
  liveLoad: number;
  deadLoad: number;
  windLoad: number;
  snowLoad: number;
  confirmedLoadRating?: boolean;
  engineeringFees?: EngineeringFees;
  checkedOutDate?: string;
  checkedOutBy?: number;
  orderSubmittedDate?: string;
  inQueueDate?: string;
  inQueueBy?: number;
  onHoldDate?: string;
  onHoldBy?: number;
  previousStatusBeforeHold?: number;
  shippedDate?: string;
  shippedBy?: number;
  readyForShipmentDate?: string;
  readyForShipmentBy?: number;
  completionDate?: string;
  statusChangedAt?: string;
  statusChangedBy?: number;
  dueDateChangedAt?: string;
  dueDateChangedBy?: number;
  lastModifiedAt?: string;
  lastModifiedBy?: number;
  statusHistory?: OrderStatusHistoryEntry[];
  notes?: string;
  signedBy?: string;
  signedDate?: string;
  qcSignOffBy?: number;
  jobLocation?: JobLocation;
  asceData?: AsceData;
  addressValidationOverride?: boolean;
  manualLoadRatingCertified?: boolean;
}

export interface OrderStatusHistoryEntry {
  id: number;
  fromStatus: number;
  toStatus: number;
  changedAt: string;
  changedByUserId?: number;
  changedByUserName?: string;
}

export const orderSchema = z.object({
  id: z.number().optional(),
  orderNumber: z.string().min(2, 'Order number is required'),
  dealerId: z.number(),
  po: poSchema,
  orderDate: createDateSchema('Order date'),
  dueDate: createOptionalDateSchema('Due date'),
  jobName: jobNameSchema,
  status: z.number(),
  billingAddress: addressSchema,
  shippingAddress: addressSchema.optional(),
  pickup: z.boolean().optional(),
  buildingState: z.string().min(1, 'Building state is required'),
  liveLoad: liveLoadSchema,
  deadLoad: deadLoadSchema,
  windLoad: windLoadSchema,
  snowLoad: snowLoadSchema,
  confirmedLoadRating: z.boolean().optional(),
});

// Partial schemas for different dialog sections
export const orderDetailsSchema = orderSchema.pick({
  orderNumber: true,
  po: true,
  jobName: true,
  orderDate: true,
  dueDate: true,
});

export const orderShippingSchema = z
  .object({
    pickup: z.boolean().optional(),
    shippingAddress: addressSchema.optional(),
    useBillingAddress: z.boolean().optional(),
    billingAddress: addressSchema,
    buildingState: z.string().optional(),
    stateConfirmed: z.boolean().optional(),
  })
  .refine(
    data => {
      // If pickup is true, no shipping address needed
      if (data.pickup === true) {
        return true;
      }

      // If useBillingAddress is true, no shipping address needed
      if (data.useBillingAddress === true) {
        return true;
      }

      // If neither pickup nor useBillingAddress, then shippingAddress must be provided and valid
      return data.shippingAddress !== undefined;
    },
    {
      message: 'Shipping address is required when not using pickup or billing address',
      path: ['shippingAddress'],
    }
  )
  .refine(
    data => {
      if (data.pickup === true) {
        return true;
      }

      let shippingState;
      if (data.useBillingAddress === true) {
        shippingState = data.billingAddress.state;
      } else {
        shippingState = data.shippingAddress?.state ? data.shippingAddress.state : '';
      }

      const buildingState = data.buildingState;

      // If either state is missing, let other validations handle it
      if (!shippingState || !buildingState) {
        return true;
      }

      // If states match, no confirmation needed
      if (shippingState === buildingState) {
        return true;
      }

      // States differ: confirmation checkbox must be checked
      return data.stateConfirmed === true;
    },
    {
      message: 'Please confirm that shipping state differs from building state.',
      path: ['stateConfirmed'],
    }
  );

export const orderLoadRatingSchema = z
  .object({
    pickup: z.boolean().optional(),
    buildingState: stateSchema,
    shippingAddress: addressSchema.optional(),
    billingAddress: addressSchema,
    stateConfirmed: z.boolean().optional(),
    liveLoad: z.number().min(0.1, 'Must be a positive number'),
    deadLoad: z.number().min(0.1, 'Must be a positive number'),
    windLoad: z
      .number()
      .int('Wind load must be a whole number')
      .min(1, 'Wind load must be at least 1'),
    snowLoad: z
      .number()
      .int('Snow load must be a whole number')
      .min(1, 'Snow load must be at least 1'),
    confirmedLoadRating: z.boolean().refine(val => val, {
      message: 'Load rating must be confirmed',
    }),
  })
  .refine(
    data => {
      if (data.pickup === true) {
        return true;
      }
      let shippingState;
      if (!data.shippingAddress?.state) {
        shippingState = data.billingAddress.state;
      } else {
        shippingState = data.shippingAddress?.state ? data.shippingAddress.state : '';
      }

      const buildingState = data.buildingState;

      // If either state is missing, let other validations handle it
      if (!shippingState || !buildingState) {
        return true;
      }

      // If states match, no confirmation needed
      if (shippingState === buildingState) {
        return true;
      }

      // States differ: confirmation checkbox must be checked
      return data.stateConfirmed === true;
    },
    {
      message: 'Please confirm that shipping state differs from building state.',
      path: ['stateConfirmed'],
    }
  );

// Type inference for the partial schemas
export type OrderDetailsFormData = z.infer<typeof orderDetailsSchema>;
export type OrderShippingFormData = z.infer<typeof orderShippingSchema>;
export type OrderLoadRatingFormData = z.infer<typeof orderLoadRatingSchema>;

// Schema map for easy access
export const ORDER_SCHEMAS = {
  details: orderDetailsSchema,
  shipping: orderShippingSchema,
  loadRating: orderLoadRatingSchema,
} as const;

// Type for schema keys
export type OrderSchemaKey = keyof typeof ORDER_SCHEMAS;

export const getBaseSchemaKeys = (schemaKey: OrderSchemaKey): string[] => {
  switch (schemaKey) {
    case 'details':
      return ['orderNumber', 'po', 'jobName', 'orderDate', 'dueDate'];
    case 'shipping':
      return [
        'pickup',
        'shippingAddress',
        'useBillingAddress',
        'billingAddress',
        'buildingState',
        'stateConfirmed',
      ];
    case 'loadRating':
      return [
        'pickup',
        'buildingState',
        'shippingAddress',
        'billingAddress',
        'stateConfirmed',
        'liveLoad',
        'deadLoad',
        'windLoad',
        'snowLoad',
        'confirmedLoadRating',
      ];
    default:
      return [];
  }
};

export interface OrderListingResponse {
  id: number;
  manufacturerId?: number;
  licenseeName?: string;
  customerName: string;
  dealerId: number;
  po: string;
  orderNumber: string;
  jobName: string;
  orderDate: string;
  dueDate: string | null;
  billingAddress: DealerLocation | null;
  shippingAddress: DealerLocation | null;
  status: string;
  liveLoad: number;
  deadLoad: number;
  windLoad: number;
  snowLoad: number;
  confirmedLoadRating: boolean;
  buildings: Building[] | null;
  allocations: unknown[] | null;
}

export interface Allocations {
  orderId: number;
  date: string;
  sizeInInches: number;
}

export interface Building {
  id: number;
  orderId: number;
  name: string;
  width?: number;
  length?: number;
  height?: number;
  baySpacing: number;
  postSize: string;
  widthMeasurementReference: string;
  application: string;
  additionalNotes?: string;
  isPermanentDwelling: boolean;
  includePermanentDwellingWithPoleBarn?: boolean;
  includePermanentDwellingForPartiallyClosedPoleBarn?: boolean;
  includePermanentDwellingForFullyEnclosedPoleBarn?: boolean;
  includePermanentDwellingWithNoMEP?: boolean;
  totalPrice?: number;
  asceWindLoadMph?: number;
  asceSnowLoadPsf?: number;
  asceRiskLevel?: number;
  trusses?: Truss[];
  trussCount?: number;
}

export interface BuildingWithoutTrusses {
  id: number;
  orderId: number;
  name: string;
  confirmedLoadRating?: boolean;
  width?: number;
  length?: number;
  height?: number;
  baySpacing: number;
  postSize: string;
  widthMeasurementReference: string;
  application: string;
  additionalNotes?: string;
  isPermanentDwelling: boolean;
  includePermanentDwellingWithPoleBarn?: boolean;
  includePermanentDwellingForPartiallyClosedPoleBarn?: boolean;
  includePermanentDwellingForFullyEnclosedPoleBarn?: boolean;
  includePermanentDwellingWithNoMEP?: boolean;
  totalPrice?: number;
  asceWindLoadMph?: number;
  asceSnowLoadPsf?: number;
  asceRiskLevel?: number;
}

export interface BuildingWithTrusses {
  id: number;
  orderId: number;
  name: string;
  confirmedLoadRating?: boolean;
  width?: number;
  length?: number;
  height?: number;
  baySpacing: number;
  postSize: string;
  widthMeasurementReference: string;
  application: string;
  additionalNotes?: string;
  isPermanentDwelling: boolean;
  includePermanentDwellingWithPoleBarn?: boolean;
  includePermanentDwellingForPartiallyClosedPoleBarn?: boolean;
  includePermanentDwellingForFullyEnclosedPoleBarn?: boolean;
  includePermanentDwellingWithNoMEP?: boolean;
  totalPrice?: number;
  asceWindLoadMph?: number;
  asceSnowLoadPsf?: number;
  asceRiskLevel?: number;
  gableTrusses?: GableTruss[];
  headerTrusses?: HeaderTruss[];
  leanToTrusses?: LeanToTruss[];
}

// Base truss properties
export interface TrussBase {
  id: number;
  buildingId: number;
  productType: 'Gable' | 'Header' | 'LeanTo';
  classification: string;
  sizeFeet: number;
  sizeInches: number;
  quantity: number;
  measurementInsidePost: number;
  confirmedMeasurementInsidePost: boolean;
  carriageBolts: boolean;
  notes?: string;
  price: number;
  stateChangeConfirmation?: boolean | null;
}

export interface GableTruss extends TrussBase {
  productType: 'Gable';
  roofPitch: string;
  overhangType: 'Standard' | 'Custom';
  overhangSize?: number;
}

export interface HeaderTruss extends TrussBase {
  productType: 'Header';
}

export interface LeanToTruss extends TrussBase {
  productType: 'LeanTo';
  roofPitch: string;
  overhangType: 'Standard' | 'Custom';
  overhangSize?: number;
  monoslopeTruss: boolean;
  postSizeOverride?: boolean;
  postSizeOverrideValue?: string | null;
  connectToHeaderTruss: boolean;
}

// Union type for all truss types
export type Truss = GableTruss | HeaderTruss | LeanToTruss;

// Request DTOs for creating/updating trusses
export interface TrussRequest {
  id: number; // 0 for new truss, > 0 for existing
  productType: 'Gable' | 'Header' | 'LeanTo';
  classification: string;
  orderDate: string; // ISO date string
  sizeFeet: number;
  sizeInches: number;
  quantity: number;
  measurementInsidePost: number;
  confirmedMeasurementInsidePost: boolean;
  roofPitch?: string; // Required for Gable and LeanTo
  overhangType?: 'Standard' | 'Custom'; // Required for Gable and LeanTo
  overhangSize?: number;
  carriageBolts: boolean;
  notes?: string;
  monoslopeTruss?: boolean; // For LeanTo
  postSizeOverride?: boolean; // For LeanTo
  postSizeOverrideValue?: string | null; // For LeanTo
  connectToHeaderTruss?: boolean; // For LeanTo
  price: number;
}

export interface BuildingRequest {
  orderId: number;
  name: string;
  confirmedLoadRating: boolean;
  width?: number;
  length?: number;
  height?: number;
  baySpacing: number;
  postSize: string;
  widthMeasurementReference: string;
  application: string;
  additionalNotes?: string;
  isPermanentDwelling: boolean;
  includePermanentDwellingWithPoleBarn?: boolean;
  includePermanentDwellingForPartiallyClosedPoleBarn?: boolean;
  includePermanentDwellingForFullyEnclosedPoleBarn?: boolean;
  includePermanentDwellingWithNoMEP?: boolean;
  asceWindLoadMph?: number;
  asceSnowLoadPsf?: number;
  asceRiskLevel?: number;
  trusses?: TrussRequest[];
  trussesProvided?: boolean;
}

export interface OrderFilters {
  licenseeName?: string;
  orderNumber?: string;
  po?: string;
  dealer?: string;
  jobName?: string;
  orderDate?: string;
  status?: string;
}

export interface OrderHeaderDetails {
  customerName: string;
  dealerId: number;
  manufacturerId?: number;
  po: string;
  jobName: string;
  notes?: string;
  isRush?: boolean;
  contactName?: string;
  contactPhone?: string;
  contactEmail?: string;
}

export interface CloneBuildingRequest {
  sourceBuildingId: number;
  targetOrderId: number;
}

export enum OrderStatus {
  DRAFT = 1,
  CHECKED_OUT = 2,
  IN_QUEUE = 3,
  SAW = 4,
  WELD = 5,
  PAINT = 6,
  ON_HOLD = 7,
  COMPLETE = 8,
  READY_FOR_PICKUP = 9,
  READY_FOR_SHIPMENT = 10,
  SHIPPED = 11,
}

export enum LoadUnitLabels {
  WEIGHT = 'lb/sqft',
  WIND = 'mph',
  SNOW = 'psf',
}

export interface OrderDueSummary {
  id: number;
  orderNumber: string;
  dealerName?: string;
  customer: string;
  dueDate: string;
  status: number;
  total: number;
}

export interface OrdersDueReportResponse {
  totalCount: number;
  orders: OrderDueSummary[];
}

export const ORDER_STATUS_DISPLAY: Record<number, string> = {
  [OrderStatus.DRAFT]: 'Draft',
  [OrderStatus.CHECKED_OUT]: 'Checked Out',
  [OrderStatus.IN_QUEUE]: 'In Queue',
  [OrderStatus.SAW]: 'In Production',
  [OrderStatus.WELD]: 'In Production',
  [OrderStatus.PAINT]: 'In Production',
  [OrderStatus.ON_HOLD]: 'On Hold',
  [OrderStatus.COMPLETE]: 'Completed',
  [OrderStatus.READY_FOR_PICKUP]: 'Ready to Ship',
  [OrderStatus.READY_FOR_SHIPMENT]: 'Ready for Shipment',
  [OrderStatus.SHIPPED]: 'Shipped',
};

export enum PastDueSeverity {
  LATE = 'Late',
  OVERDUE = 'Overdue',
  CRITICAL = 'Critical',
}

export interface OrderPastDueSummary {
  id: number;
  severity: PastDueSeverity;
  orderNumber: string;
  dealerName?: string;
  customer: string;
  dueDate: string;
  daysLate: number;
  status: number;
  total: number;
}

export interface OrdersPastDueReportResponse {
  totalCount: number;
  orders: OrderPastDueSummary[];
}

export interface ProductSummary {
  totalQuantity: number;
  trussSizeFt: number;
  /** Fractional inches (0–11.5); combined with trussSizeFt for display (UDOMI-1046). */
  trussSizeInches: number;
  pitch: string | null;
  postSize: string;
  trussType: string;
  overhang: string | null;
  overhangCustom: number | null;
}

export interface ProductsReportResponse {
  products: ProductSummary[];
}

export interface OrderSignRequest {
  signedBy: string;
}

export interface TravelerCompletionRequest {
  /** Completion date in YYYY-MM-DD format, or null to clear. */
  completionDate: string | null;
  /** QC sign-off technician id, or null to clear. */
  qcSignOffBy: number | null;
}

export interface CheckoutRequest {
  proposedDueDate?: string;
}

export interface CheckoutResponse {
  orderId: number;
  orderNumber: string;
  status: number;
  dueDate: string;
  checkedOutDate: string;
  checkedOutBy: number;
  orderSubmittedDate: string;
  totalPrice: number;
  leadTimeDays: number;
  maxLinealFeetPerDay: number;
}

export interface CheckoutValidationResponse {
  isValid: boolean;
  errors: string[];
  calculatedDueDate?: string;
  leadTimeDays: number;
  maxLinealFeetPerDay: number;
  orderTotalPrice: number;
  orderTotalFeet: number;
}
